// P05-4-2: Coach Directory Page
// Displays verified coaches with filtering and follow functionality

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Users, Globe, Award, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { callEdge } from '@/lib/edgeClient';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

interface Coach {
  id: string;
  user_id: string;
  full_name: string | null;
  coach_bio: string | null;
  specialties: string[] | null;
  certifications: string[] | null;
  website_url: string | null;
  follower_count: number;
  created_at: string;
}

interface CoachListResponse {
  coaches: Coach[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    has_more: boolean;
  };
}

async function fetchCoaches(limit: number, offset: number, specialty?: string): Promise<CoachListResponse> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL not configured');
  }
  if (!anonKey) {
    throw new Error('VITE_SUPABASE_ANON_KEY not configured');
  }

  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  });
  if (specialty) {
    params.append('specialty', specialty);
  }

  const url = `${supabaseUrl}/functions/v1/coach-list?${params.toString()}`;
  console.log('[CoachDirectoryPage] Fetching coaches from:', url);
  
  const response = await fetch(url, {
    headers: {
      'apikey': anonKey,
      'Content-Type': 'application/json',
    },
  });
  console.log('[CoachDirectoryPage] Response status:', response.status, response.statusText);
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('[CoachDirectoryPage] Error response:', errorText);
    let errorMessage = 'Failed to fetch coaches';
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(`${response.status}: ${errorMessage}`);
  }
  
  const data = await response.json();
  console.log('[CoachDirectoryPage] Received coaches:', data);
  return data;
}

async function toggleFollow(followedId: string): Promise<{ following: boolean }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL not configured');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/feed-follow-toggle`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ followed_id: followedId }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = 'Failed to toggle follow';
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export default function CoachDirectoryPage() {
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [followingMap, setFollowingMap] = useState<Record<string, boolean>>({});
  const { toast } = useToast();
  const limit = 20;

  const { data, isLoading, error, refetch } = useQuery<CoachListResponse>({
    queryKey: ['coaches', selectedSpecialty, page],
    queryFn: () => fetchCoaches(limit, page * limit, selectedSpecialty === 'all' ? undefined : selectedSpecialty),
  });

  // Check which coaches user is following
  useEffect(() => {
    async function checkFollowing() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: relationships } = await supabase
        .from('user_relationships')
        .select('followed_id')
        .eq('follower_id', session.user.id);

      if (relationships) {
        const following: Record<string, boolean> = {};
        relationships.forEach((rel) => {
          following[rel.followed_id] = true;
        });
        setFollowingMap(following);
      }
    }

    checkFollowing();
  }, [data]);

  // Extract unique specialties from coaches
  const specialties = Array.from(
    new Set(
      data?.coaches.flatMap((c) => c.specialties || []) || []
    )
  ).sort();

  const handleFollowToggle = async (coachId: string) => {
    try {
      const result = await toggleFollow(coachId);
      setFollowingMap((prev) => ({
        ...prev,
        [coachId]: result.following,
      }));
      toast({
        title: result.following ? 'Following coach' : 'Unfollowed coach',
        description: result.following
          ? "You'll see their posts in your feed"
          : "You won't see their posts anymore",
      });
      refetch();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to toggle follow',
        variant: 'destructive',
      });
    }
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (error) {
    console.error('[CoachDirectoryPage] Error loading coaches:', error);
    return (
      <div className="text-center py-12">
        <p className="text-destructive font-semibold">Failed to load coaches</p>
        <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
        <details className="mt-4 text-left max-w-md mx-auto">
          <summary className="cursor-pointer text-sm text-muted-foreground">Error details</summary>
          <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
            {JSON.stringify(error, null, 2)}
          </pre>
        </details>
        <Button variant="outline" className="mt-4" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const coaches = data?.coaches || [];
  const hasMore = data?.pagination.has_more || false;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Discover Coaches</h1>
        <p className="text-muted-foreground mt-2">
          Connect with verified golf coaches and learn from the best
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by specialty" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Specialties</SelectItem>
            {specialties.map((spec) => (
              <SelectItem key={spec} value={spec}>
                {spec}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground">
          {data?.pagination.total || 0} coaches found
        </div>
      </div>

      {/* Coach Grid */}
      {coaches.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-lg font-semibold">No coaches found</p>
          <p className="text-muted-foreground mt-2">
            {selectedSpecialty !== 'all'
              ? 'Try a different specialty filter'
              : 'Check back later for verified coaches'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coaches.map((coach) => {
              const isFollowing = followingMap[coach.user_id] || false;
              const initials = coach.full_name
                ?.split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2) || '??';

              return (
                <Card key={coach.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback>{initials}</AvatarFallback>
                        </Avatar>
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {coach.full_name || 'Coach'}
                            <CheckCircle className="w-4 h-4 text-emerald-600" />
                          </CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-1">
                            <Users className="w-3 h-3" />
                            {coach.follower_count} followers
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {coach.coach_bio && (
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {coach.coach_bio}
                      </p>
                    )}

                    {coach.specialties && coach.specialties.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {coach.specialties.map((spec) => (
                          <Badge key={spec} variant="secondary">
                            {spec}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {coach.certifications && coach.certifications.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Award className="w-4 h-4" />
                        <span>{coach.certifications.join(', ')}</span>
                      </div>
                    )}

                    {coach.website_url && (
                      <a
                        href={coach.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-emerald-600 hover:underline"
                      >
                        <Globe className="w-4 h-4" />
                        Visit website
                      </a>
                    )}

                    <Button
                      variant={isFollowing ? 'outline' : 'default'}
                      className="w-full"
                      onClick={() => handleFollowToggle(coach.user_id)}
                    >
                      {isFollowing ? 'Unfollow' : 'Follow'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Pagination */}
          {hasMore && (
            <div className="flex justify-center pt-6">
              <Button
                variant="outline"
                onClick={() => setPage((p) => p + 1)}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

